import {
  useQuery,
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions
} from '@tanstack/react-query';

import { ELEMENTS_ARTICLES } from '@constants/ElementsLimit';
import type { GetArticleByIdQuery } from '@schema/cart';

import API from '@api';
import { withSsrCache } from '../../ssrCache';

export const useArticlesQuery = (
  params: Parameters<typeof API.getArticles>[0]
) => {
  const cacheKey = ['articles', params];

  return useQuery({
    queryKey: cacheKey,
    queryFn: () => API.getArticles(params),
    placeholderData: keepPreviousData
  });
};

export interface ArticleHeading {
  text: string;
  id: string;
  subHeadings: Array<{ text: string; id: string }>;
}

const ARTICLES_STALE_TIME = 5 * 60 * 1000;
const ARTICLES_GC_TIME = 10 * 60 * 1000;

/** Базовые html-сущности в текстах оглавления (innerText их декодировал) */
const decodeEntities = (value: string): string =>
  value
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#0?39;/g, "'");

/**
 * Проставляет id заголовкам h2/h3 в html статьи и собирает оглавление.
 *
 * ЭТАП 2 — переписано ИЗОМОРФНО: было document.createElement + uuid v1, но
 * select выполняется и на SSR при чтении кэша (document на сервере нет), а
 * случайные id давали бы hydration mismatch на dangerouslySetInnerHTML и
 * оглавлении. Regex по h2/h3 + детерминированные id (по порядку заголовка) —
 * сервер и клиент считают одинаково из одного текста.
 */
const addIdToH2 = (
  text: string
): { headings: ArticleHeading[]; text: string } => {
  const tocArray: ArticleHeading[] = [];
  let counter = 0;

  const withIds = text.replace(
    /<(h2|h3)\b([^>]*)>([\s\S]*?)<\/\1>/gi,
    (_full, tag: string, attrs: string, inner: string) => {
      counter += 1;
      const id = `article-heading-${counter}`;
      const level = tag.toLowerCase();
      const plainText = decodeEntities(
        inner.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim()
      );

      if (level === 'h2') {
        tocArray.push({ text: plainText, id, subHeadings: [] });
      } else if (tocArray.length > 0) {
        tocArray[tocArray.length - 1].subHeadings.push({ text: plainText, id });
      }

      // как и раньше (heading.id = id): существующий id перекрывается
      const cleanedAttrs = attrs.replace(
        /\sid=("[^"]*"|'[^']*'|[^\s>]+)/gi,
        ''
      );
      return `<${level} id="${id}"${cleanedAttrs}>${inner}</${level}>`;
    }
  );

  // <article>-обёртка — как у старого el.outerHTML
  return { headings: tocArray, text: `<article>${withIds}</article>` };
};

// module-level: стабильная ссылка, иначе select пересчитывался бы каждый рендер
const selectArticleById = (res: GetArticleByIdQuery) => {
  const { text, headings } = addIdToH2(res.getArticleById?.text || '');

  return {
    article: { ...res.getArticleById, text },
    headings
  };
};

export const articleByIdQueryOptions = (id: string) =>
  withSsrCache(
    queryOptions({
      queryKey: ['articleById', id],
      queryFn: () => API.getArticleById({ id }),
      select: selectArticleById,
      staleTime: ARTICLES_STALE_TIME,
      gcTime: ARTICLES_GC_TIME
    })
  );

export const articleRelatedCatalogItemsQueryOptions = (
  articleId: number,
  xStoreId: number | null
) =>
  withSsrCache(
    queryOptions({
      queryKey: ['articleRelatedCatalogItems', articleId, xStoreId],
      queryFn: () => API.getArticleRelatedCatalogItems({ articleId }),
      select: res => res.getArticleRelatedCatalogItems ?? [],
      staleTime: ARTICLES_STALE_TIME,
      gcTime: ARTICLES_GC_TIME,
      // ЭТАП 2 — xStoreId в ключе: после SSR (pristine null) магазин доедет и
      // сменит ключ — без плейсхолдера isPending ронял всю статью в скелетоны
      placeholderData: keepPreviousData
    }),
    'product',
    [xStoreId]
  );

export const articleRelatedArticlesQueryOptions = (id: number) =>
  withSsrCache(
    queryOptions({
      queryKey: ['articleRelatedArticles', id],
      queryFn: () => API.getArticleRelatedArticles({ id }),
      select: res => res.getArticleRelatedArticles ?? [],
      staleTime: ARTICLES_STALE_TIME,
      gcTime: ARTICLES_GC_TIME
    })
  );

export const articlesInfiniteQueryOptions = (
  params: Omit<Parameters<typeof API.getArticles>[0], 'page'>,
  initialPage = 1
) => {
  const cacheKey = ['articles', 'infinite', params];

  return withSsrCache(
    infiniteQueryOptions({
      queryKey: cacheKey,
      queryFn: ({ pageParam }) => {
        const perPage = params.setLimit ?? ELEMENTS_ARTICLES;

        return API.getArticles({ ...params, page: pageParam }).then(res => {
          const totalCountPages = Math.ceil(
            res.getArticles.totalCount / perPage
          );

          return {
            items: res.getArticles.articlesItems || [],
            total: res.getArticles.totalCount,
            pagination: {
              total: totalCountPages,
              current: pageParam
            }
          };
        });
      },
      initialPageParam: initialPage,
      getNextPageParam: (lastPage, _allPages, lastPageParam) => {
        const totalCount = lastPage.total;
        const limit = params.setLimit ?? ELEMENTS_ARTICLES;

        return lastPageParam * limit < totalCount
          ? lastPageParam + 1
          : undefined;
      },
      placeholderData: keepPreviousData
    })
  );
};

/**
 * ЭТАП 2 — параметры списка статей (/lenta): ЕДИНЫЙ источник для компонента
 * (routes/Articles) и SSR-префетча — наличие/отсутствие search в объекте
 * ключа обязано совпасть байт-в-байт.
 */
export const buildArticlesListParams = (
  search: string | undefined
): { search?: string; setLimit: number } => ({
  ...(search ? { search } : {}),
  setLimit: ELEMENTS_ARTICLES
});
